import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import Landmark from '@core/scripts/components/landmark-adapter';
import Device from '@core/scripts/components/device';
import {useDispatch} from 'react-redux';
import {loadOn} from '../LoaderSlice.jsx';
import {yieldToMain} from '../ArticleOverviewHelper';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {Icon, Link} from '@bonprix/design-system';
import {Constants} from '../view-constants.js';
import {useBreakpointUp} from 'bonprix-design-system';

export default function Subshops({
    identifier,
    subshops,
    links,
    trackingId,
    rootShopName,
    filterSubmit,
    subshopsTopOffset,
}) {
    const {shopId, rootCategoryPath} = useContext(articleOverviewStaticContext);
    const dispatch = useDispatch();
    const isXLargeViewportSticky = useBreakpointUp('xl');

    const subshopsRef = useRef(null);
    const sliderRef = useRef(null);
    const [showPrev, setShowPrev] = useState(false);
    const [showNext, setShowNext] = useState(false);

    useEffect(() => {
        if (!subshopsRef.current) return;
        const top = isXLargeViewportSticky ? subshopsTopOffset - 1 : '';
        subshopsRef.current.style.top = top ? `${top}px` : '';
    }, [subshopsTopOffset, isXLargeViewportSticky]);

    useEffect(() => {
        scrollSelectedSubshopToCenter();
        updateButtonVisibility();
    }, [subshops]);

    const updateButtonVisibility = () => {
        const slider = sliderRef.current;
        if (slider && !Device.isTouch()) {
            setShowPrev(slider.scrollLeft > 0);
            setShowNext(slider.scrollLeft + slider.clientWidth < slider.scrollWidth - 1);
        } else {
            setShowPrev(false);
            setShowNext(false);
        }
    };

    const handleNextClick = () => {
        const slider = sliderRef.current;
        if (slider) {
            slider.scrollBy({left: slider.clientWidth, behavior: 'smooth'});
        }
    };

    const handlePrevClick = () => {
        const slider = sliderRef.current;
        if (slider) {
            slider.scrollBy({left: -slider.clientWidth, behavior: 'smooth'});
        }
    };

    const scrollSelectedSubshopToCenter = () => {
        const slider = sliderRef.current;
        if (slider) {
            const selectedSubshop = slider.querySelector('.subshops__slide.-active');
            if (selectedSubshop) {
                const sliderWidth = slider.clientWidth;
                const subshopWidth = selectedSubshop.clientWidth;
                const subshopOffsetLeft = selectedSubshop.offsetLeft;
                const scrollPosition = subshopOffsetLeft - sliderWidth / 2 + subshopWidth / 2;
                slider.scrollTo({left: scrollPosition, behavior: 'smooth'});
            }
        }
    };

    const slides = [];
    if (subshops) {
        slides.push(renderRootShopItem());
    }
    subshops?.map((subshop) => {
        slides.push(renderSubshopItem(subshop));
    });
    links?.map((link) => {
        slides.push(renderShopLink(link));
    });

    const selectSubshop = useCallback(
        (subshop, disabled) => {
            yieldToMain(() => dispatch(loadOn()));
            const filterData = new Map();
            filterData.set(identifier, {trackingId, clickedFilterOptions: [subshop.value], path: subshop.categoryPath});
            filterSubmit(filterData, undefined, disabled);
        },
        [identifier, trackingId, filterSubmit],
    );

    const selectRootShop = useCallback(() => {
        yieldToMain(() => dispatch(loadOn()));
        const filterData = new Map();
        filterData.set(identifier, {trackingId, clickedFilterOptions: [], path: rootCategoryPath.current});
        filterSubmit(filterData);
    }, [identifier, trackingId, filterSubmit]);

    return (
        <section className="article-overview__subshops" ref={subshopsRef}>
            {showPrev && (
                <button
                    className="subshops__slider-btn bg-clr-on-primary-high -prev"
                    onClick={handlePrevClick}
                    aria-label={Constants.ARIA_LABEL_SUBSHOPS_PREV}
                >
                    <Icon variant={'arrow-left'} />
                </button>
            )}
            <div className="subshops__slider" ref={sliderRef} onScroll={updateButtonVisibility}>
                {slides}
            </div>
            {showNext && (
                <button
                    className="subshops__slider-btn bg-clr-on-primary-high -next"
                    onClick={handleNextClick}
                    aria-label={Constants.ARIA_LABEL_SUBSHOPS_NEXT}
                >
                    <Icon variant={'arrow-right'} />
                </button>
            )}
        </section>
    );

    function renderRootShopItem() {
        const selected = !subshops?.some((subshop) => subshop.selected);

        return (
            <button
                className={`subshops__slide text-clr-primary${selected ? ' -active' : ''}`}
                onClick={() => selectRootShop()}
            >
                <span
                    className={`subshops__slide-text hover:border-b-clr-primary${selected ? ' border-b-clr-primary border-b-4' : ''}`}
                >
                    <span className="subshops__slide-focus-wrapper">Alle {rootShopName}</span>
                </span>
            </button>
        );
    }

    function renderSubshopItem(subshop) {
        const disabled = subshop.numberOfItems === 0;

        const handleOnClick = (event) => {
            event.preventDefault();
            selectSubshop(subshop, disabled);
        };

        return (
            <a
                className={`subshops__slide${disabled ? ' text-clr-on-bg-low' : ' text-clr-primary'}`}
                onClick={handleOnClick}
                href={subshop.link}
            >
                <span
                    className={`subshops__slide-text${subshop.selected ? ' border-b-clr-primary border-b-4' : ''}${disabled ? ' hover:border-b-clr-on-bg-low' : ' hover:border-b-clr-primary'}`}
                >
                    <span className="subshops__slide-focus-wrapper">{subshop.name}</span>
                </span>
            </a>
        );
    }

    function renderShopLink(link) {
        const handleOnClick = (event) => {
            event.preventDefault();
            navigateToShopLandmark(link.id);
            window.location.assign(link.path);
        };

        return (
            <div className="subshops__link-slide">
                <Link href={link.path} onClick={handleOnClick}>
                    {link.name}
                </Link>
            </div>
        );
    }

    function navigateToShopLandmark(redirectDestination) {
        const redirectOrigin = shopId.current ? shopId.current : 0;
        let landmarkData = {
            landmark: 'userinterface',
            redirect_origin: `${redirectOrigin}`,
            redirect_destination: `${redirectDestination}`,
        };
        Landmark.sendLandmark(landmarkData);
    }
}
